import React, { useState } from 'react';
import { Table, Button, Space, Tag } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDownload, faSync } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';

const CallVolumeTable = ({ data, loading, onRefresh }) => {
  const [sortedInfo, setSortedInfo] = useState({});

  const handleChange = (pagination, filters, sorter) => {
    setSortedInfo(sorter);
  };

  // Ensure data is an array
  const rawData = Array.isArray(data) ? data : [];
  
  // Check if we have any grouped campaigns in the data
  const hasGroupedData = rawData.some(item => item.groupedCampaigns && item.groupedCampaigns.length > 0);
  
  // Filter data: if grouped data exists, only show grouped records; otherwise show all
  const filteredData = hasGroupedData 
    ? rawData.filter(item => item.groupedCampaigns && item.groupedCampaigns.length > 0)
    : rawData;
  
  // Group data by date + campaign for grouped view
  const groupedData = filteredData.reduce((acc, item) => {
    const dateKey = moment(item.date).format('YYYY-MM-DD');
    const campaign = item.campaignName || 'Unknown';
    const key = `${dateKey}-${campaign}`;
    
    if (!acc[key]) {
      acc[key] = {
        date: item.date,
        campaignName: campaign,
        ringCentralCalls: item.ringCentralCalls || 0,
        googleAdsCalls: item.googleAdsCalls || 0,
        totalCost: item.totalCost || 0,
        conversions: item.conversions || 0,
        callDiscrepancy: item.callDiscrepancy || 0,
        groupedCampaigns: item.groupedCampaigns || [],
        campaigns: []
      };
    }
    
    // If this is grouped data with campaign details, store them
    if (item.groupedCampaigns && item.groupedCampaigns.length > 0) {
      acc[key].isGrouped = true;
      acc[key].groupedCampaigns = item.groupedCampaigns;
    }
    
    return acc;
  }, {});
  
  const tableData = Object.values(groupedData);

  const exportToCSV = () => {
    if (tableData.length === 0) {
      return;
    }

    // Create CSV content
    const headers = ['Date', 'Campaign', 'RingCentral Calls', 'Google Ads Calls', 'Discrepancy', 'Discrepancy %', 'Cost Per Call'];
    const rows = tableData.map(item => [
      moment(item.date).format('YYYY-MM-DD'),
      item.campaignName || 'Unknown',
      item.ringCentralCalls || 0,
      item.googleAdsCalls || 0,
      item.callDiscrepancy || 0,
      item.discrepancyPercentage ? `${item.discrepancyPercentage.toFixed(2)}%` : '0%',
      item.costPerCall ? `$${item.costPerCall.toFixed(2)}` : '-'
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    // Create and trigger download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `call-volume-analytics-${moment().format('YYYY-MM-DD')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: '12%',
      sorter: (a, b) => moment(a.date).unix() - moment(b.date).unix(),
      sortOrder: sortedInfo.columnKey === 'date' ? sortedInfo.order : null,
      render: (date) => moment(date).format('MMM DD, YYYY')
    },
    {
      title: 'Campaign',
      dataIndex: 'campaignName',
      key: 'campaignName',
      width: '20%',
      sorter: (a, b) => (a.campaignName || '').localeCompare(b.campaignName || ''),
      sortOrder: sortedInfo.columnKey === 'campaignName' ? sortedInfo.order : null,
      render: (name) => name || <span style={{ color: '#999' }}>Unknown</span>
    },
    {
      title: 'RingCentral',
      dataIndex: 'ringCentralCalls',
      key: 'ringCentralCalls',
      width: '12%',
      align: 'right',
      sorter: (a, b) => (a.ringCentralCalls || 0) - (b.ringCentralCalls || 0),
      sortOrder: sortedInfo.columnKey === 'ringCentralCalls' ? sortedInfo.order : null,
      render: (calls) => (
        <span style={{ color: '#1890ff', fontWeight: 'bold' }}>
          {calls || 0}
        </span>
      )
    },
    {
      title: 'Google Ads',
      dataIndex: 'googleAdsCalls',
      key: 'googleAdsCalls',
      width: '12%',
      align: 'right',
      sorter: (a, b) => (a.googleAdsCalls || 0) - (b.googleAdsCalls || 0),
      sortOrder: sortedInfo.columnKey === 'googleAdsCalls' ? sortedInfo.order : null,
      render: (calls) => (
        <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
          {calls || 0}
        </span>
      )
    },
    {
      title: 'Discrepancy',
      dataIndex: 'callDiscrepancy',
      key: 'callDiscrepancy',
      width: '12%',
      align: 'right',
      sorter: (a, b) => Math.abs(a.callDiscrepancy || 0) - Math.abs(b.callDiscrepancy || 0),
      sortOrder: sortedInfo.columnKey === 'callDiscrepancy' ? sortedInfo.order : null,
      render: (discrepancy) => {
        const value = discrepancy || 0;
        const absValue = Math.abs(value);
        
        if (absValue === 0) {
          return <Tag color="default">Perfect Match</Tag>;
        } else if (value > 0) {
          return <Tag color="success">+{absValue}</Tag>;
        } else {
          return <Tag color="error">-{absValue}</Tag>;
        }
      }
    },
    {
      title: 'Discrepancy %',
      dataIndex: 'discrepancyPercentage',
      key: 'discrepancyPercentage',
      width: '12%',
      align: 'right',
      sorter: (a, b) => Math.abs(a.discrepancyPercentage || 0) - Math.abs(b.discrepancyPercentage || 0),
      sortOrder: sortedInfo.columnKey === 'discrepancyPercentage' ? sortedInfo.order : null,
      render: (percentage) => {
        const value = percentage || 0;
        const absValue = Math.abs(value);
        
        let color = '#999';
        if (absValue > 20) color = '#ff4d4f';
        else if (absValue > 10) color = '#faad14';
        
        return (
          <span style={{ color, fontWeight: absValue > 10 ? 'bold' : 'normal' }}>
            {absValue.toFixed(1)}%
          </span>
        );
      }
    },
    {
      title: 'Cost/Call',
      key: 'costPerCall',
      width: '10%',
      align: 'right',
      sorter: (a, b) => {
        const costA = a.googleAdsCalls > 0 ? (a.totalCost / a.googleAdsCalls) : 0;
        const costB = b.googleAdsCalls > 0 ? (b.totalCost / b.googleAdsCalls) : 0;
        return costA - costB;
      },
      sortOrder: sortedInfo.columnKey === 'costPerCall' ? sortedInfo.order : null,
      render: (_, record) => {
        const avgCost = record.googleAdsCalls > 0 ? record.totalCost / record.googleAdsCalls : 0;
        return avgCost > 0 ? `$${avgCost.toFixed(2)}` : '-';
      }
    },
    {
      title: 'Total Cost',
      dataIndex: 'totalCost',
      key: 'totalCost',
      width: '10%',
      align: 'right',
      sorter: (a, b) => (a.totalCost || 0) - (b.totalCost || 0),
      sortOrder: sortedInfo.columnKey === 'totalCost' ? sortedInfo.order : null,
      render: (cost) => cost ? `$${cost.toFixed(2)}` : '-'
    }
  ];

  return (
    <div>
      <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
        <div>
          <h3 style={{ margin: 0 }}>Daily Call Volume Breakdown</h3>
          <p style={{ margin: '4px 0 0 0', color: '#666', fontSize: '14px' }}>
            {data && data.length > 0 ? `Showing ${data.length} days of data` : 'No data available'}
          </p>
        </div>
        <Space>
          <Button
            icon={<FontAwesomeIcon icon={faSync} />}
            onClick={onRefresh}
            loading={loading}
          >
            Refresh
          </Button>
          <Button
            type="primary"
            icon={<FontAwesomeIcon icon={faDownload} />}
            onClick={exportToCSV}
            disabled={tableData.length === 0}
          >
            Export CSV
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={tableData}
        rowKey={(record) => `${record.date}-${record.campaignName}`}
        loading={loading}
        onChange={handleChange}
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} records`,
          pageSizeOptions: ['10', '20', '50', '100']
        }}
        size="small"
        rowClassName={(record) => {
          const discrepancy = record.callDiscrepancy || 0;
          // Green background for positive discrepancy (more calls than Google tracked)
          if (discrepancy > 0) return 'positive-discrepancy-row';
          // White/default for perfect match
          return '';
        }}
        expandable={{
          expandedRowRender: (record) => {
            if (!record.isGrouped || !record.groupedCampaigns || record.groupedCampaigns.length === 0) {
              return null;
            }
            
            // Get individual campaign data from rawData
            const dateKey = moment(record.date).format('YYYY-MM-DD');
            const individualCampaigns = rawData.filter(item => {
              const itemDate = moment(item.date).format('YYYY-MM-DD');
              return itemDate === dateKey && 
                     record.groupedCampaigns.includes(item.campaignName) &&
                     (!item.groupedCampaigns || item.groupedCampaigns.length === 0);
            });
            
            // Use the same column structure as the main table
            const detailColumns = [
              {
                title: 'Date',
                dataIndex: 'date',
                key: 'date',
                width: '12%',
                render: (date) => moment(date).format('MMM DD, YYYY')
              },
              {
                title: 'Campaign',
                dataIndex: 'campaignName',
                key: 'campaignName',
                width: '20%'
              },
              {
                title: 'RingCentral',
                key: 'ringCentralCalls',
                width: '12%',
                align: 'right',
                render: () => <span style={{ color: '#999' }}>-</span>
              },
              {
                title: 'Google Ads',
                dataIndex: 'googleAdsCalls',
                key: 'googleAdsCalls',
                width: '12%',
                align: 'right',
                render: (calls) => (
                  <span style={{ color: '#52c41a', fontWeight: 'bold' }}>
                    {calls || 0}
                  </span>
                )
              },
              {
                title: 'Discrepancy',
                key: 'discrepancy',
                width: '12%',
                align: 'right',
                render: () => <span style={{ color: '#999' }}>-</span>
              },
              {
                title: 'Discrepancy %',
                key: 'discrepancyPercentage',
                width: '12%',
                align: 'right',
                render: () => <span style={{ color: '#999' }}>-</span>
              },
              {
                title: 'Cost/Call',
                key: 'costPerCall',
                width: '10%',
                align: 'right',
                render: (_, item) => {
                  const avgCost = item.googleAdsCalls > 0 
                    ? item.totalCost / item.googleAdsCalls 
                    : 0;
                  return avgCost > 0 ? `$${avgCost.toFixed(2)}` : '-';
                }
              },
              {
                title: 'Total Cost',
                dataIndex: 'totalCost',
                key: 'totalCost',
                width: '10%',
                align: 'right',
                render: (value) => value ? `$${value.toFixed(2)}` : '-'
              }
            ];
            
            return (
              <div style={{ margin: '0 0 0 -16px', padding: '0' }}>
                <Table
                  columns={detailColumns}
                  dataSource={individualCampaigns}
                  rowKey={(item) => `${item.date}-${item.campaignName}`}
                  pagination={false}
                  size="small"
                  showHeader={false}
                  style={{ marginBottom: 0 }}
                  rowClassName="detail-row"
                />
              </div>
            );
          },
          rowExpandable: (record) => record.isGrouped && record.groupedCampaigns && record.groupedCampaigns.length > 0
        }}
      />

      <style jsx="true">{`
        .positive-discrepancy-row {
          background-color: #f6ffed !important;
        }
        .positive-discrepancy-row:hover {
          background-color: #d9f7be !important;
        }
        .detail-row {
          background-color: #fffbf0 !important;
        }
        .detail-row:hover {
          background-color: #fff4d9 !important;
        }
      `}</style>
    </div>
  );
};

export default CallVolumeTable;
